import type { BlogPost, BlogSection } from "./blog";

function fnvHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length];
}

function lower(s: string): string {
  return s.toLowerCase();
}

export function expandBody(post: BlogPost): BlogSection[] {
  const seed = fnvHash(post.slug);
  const cat = post.category;
  const catLower = lower(cat);
  const titleLower = lower(post.title.replace(/\s*\([^)]*\)\s*$/, ""));
  const kw = post.keywords ?? [];
  const focus = kw[0] ?? catLower;
  const focus2 = kw[1] ?? focus;
  const focus3 = kw[2] ?? focus2;
  const hasExistingFaq = post.body.some((s) => s.type === "faq");

  const whyHeadings = [
    `Why ${cat} Strategy Matters Right Now`,
    `Why This Matters For Auto Operators in 2026`,
    `The Strategic Stakes Around ${cat}`,
    `Why ${cat} Is A Boardroom Conversation`,
  ];
  const whatHeadings = [
    `What We See Across ${cat} Engagements`,
    `Patterns From The Field`,
    `What Actually Works In ${cat} Today`,
    `The Repeating Pattern In ${cat}`,
  ];
  const roadmapHeadings = [
    `A 90-Day Roadmap You Can Actually Run`,
    `The 90-Day Implementation Plan`,
    `How To Phase This Over A Quarter`,
    `Sequencing The Work Over 90 Days`,
  ];
  const measHeadings = [
    `Measurement, Attribution, And Reporting`,
    `Measuring What Actually Matters`,
    `Building A Measurement System That Holds`,
    `Reporting That Tells You The Truth`,
  ];
  const pitfallHeadings = [
    `Common Pitfalls That Quietly Drain Budget`,
    `Where Most Teams Lose The Plot`,
    `Mistakes We See In Almost Every Audit`,
    `The Failure Modes To Watch For`,
  ];
  const rdHeadings = [
    `How Relevant Dealer Approaches ${cat}`,
    `The Relevant Dealer Operating Model`,
    `How A Fractional CMO Runs This Work`,
    `What An Outside Operator Brings To ${cat}`,
  ];
  const closeHeadings = [
    `Bringing It Together`,
    `The Bottom Line For Operators`,
    `What To Do With This`,
    `Putting It Into Practice`,
  ];

  const sections: BlogSection[] = [
    { type: "heading", content: pick(whyHeadings, seed) },
    {
      type: "paragraph",
      content: `The pace of change in automotive marketing has accelerated through 2026, and ${catLower} sits squarely in the middle of that shift. Buyer behavior, AI-mediated search, OEM compliance pressure, and tightening dealer margins have converged in a way that makes sloppy execution genuinely expensive. The teams winning in ${catLower} treat it as a measurable system tied directly to sold units, fixed-ops gross, and pipeline health — not as a creative exercise that lives outside of the P&L. That mindset shift is what separates the dealers and OEMs that take share from the ones quietly losing it to better-prepared competitors.`,
    },
    {
      type: "paragraph",
      content: `Whether you operate at a single-rooftop store, a multi-store dealer group, or an automotive SaaS company selling into dealers, the principles in this article apply — but execution looks different at each scale. The fundamentals around ${focus} do not change. The operational footprint, the budget allocation, and the team structure required to execute well do. The rest of this piece walks through the patterns we see working today, the measurement framework we use to keep teams honest, and the pitfalls that quietly drain budget when leadership is not paying close attention.`,
    },

    { type: "heading", content: pick(whatHeadings, seed + 1) },
    {
      type: "paragraph",
      content: `Across our active engagements — franchise dealers, dealer groups, and automotive SaaS companies — five patterns repeat in nearly every ${catLower} program. Each pattern produces measurable lift when addressed and measurable drag when ignored. None of them are novel. They are simply the unglamorous fundamentals that get skipped when a team is over-rotated on the next shiny tactic or buried under day-to-day vendor management.`,
    },
    {
      type: "bullet-list",
      items: [
        `The biggest gains come from fixing data hygiene and instrumentation before changing tactics, especially when ${focus} is involved.`,
        `Vendors and platforms tend to optimize for their own metrics; the dealer or OEM has to define the outcome metrics themselves.`,
        `Most teams underinvest in measurement and overinvest in production — that ratio is almost always backwards.`,
        `Cross-functional alignment with sales, fixed ops, and finance is what makes ${catLower} budgets get approved instead of cut at the next review.`,
        `Quarterly reviews tied to sold units and pipeline outperform monthly vanity-metric dashboards every time.`,
        `Senior attention beats junior effort: an hour of operator time is worth ten hours of unmanaged execution work.`,
      ],
    },

    { type: "heading", content: pick(roadmapHeadings, seed + 2) },
    {
      type: "paragraph",
      content: `For teams getting serious about ${catLower}, a 90-day cadence is usually the right operating rhythm. Long enough for measurement to mean something, short enough to keep accountability sharp. The roadmap below is the same shape we use with new clients, adapted to the topic of "${titleLower}". It will not be a perfect fit for every situation, but it is a reasonable default — and crucially, it is a plan you can actually run with the team you already have.`,
    },
    {
      type: "bullet-list",
      items: [
        `Days 1 through 30: Audit the current state. Inventory the tools, vendors, contracts, and reporting in scope. Document baseline KPIs against ${focus} so you can measure honestly later.`,
        `Days 31 through 60: Make the highest-leverage fixes. Address the data, instrumentation, and execution gaps surfaced in the audit. Stand up a single source-of-truth dashboard tied to outcome metrics.`,
        `Days 61 through 90: Optimize and scale. With clean data flowing, run focused experiments — landing pages, audience segments, creative variants, or vendor swaps — and double down on what beats baseline.`,
        `Day 91 and onward: Move to a quarterly operating rhythm. Maintain the dashboard, run quarterly business reviews, and commit to one strategic bet per quarter that meaningfully advances ${focus2}.`,
      ],
    },

    { type: "heading", content: pick(measHeadings, seed + 3) },
    {
      type: "paragraph",
      content: `Measurement is where most ${catLower} programs quietly fall apart. The tools are there. The discipline to define outcome metrics in advance, instrument them correctly, and review them on a fixed cadence is rare. The dealers and OEMs that get the most leverage from ${focus2} treat measurement as a first-class part of the program — not an afterthought handed off to a vendor or a junior reporting analyst whose work nobody actually reads.`,
    },
    {
      type: "bullet-list",
      items: [
        `Define one north-star outcome metric that ties directly to revenue — sold units, ROs booked, qualified pipeline, or net retention.`,
        `Track three or four leading indicators that should move before the north-star metric does, so you get early warning.`,
        `Use server-side tagging or first-party data wherever possible. Third-party cookies are no longer reliable and the gap will only widen.`,
        `Reconcile platform-reported conversions against CRM and DMS data on at least a monthly cadence — they will not agree, and the differences are informative.`,
        `Build the dashboard once and review it on the same day every week. Consistency beats sophistication every time.`,
        `Tie ${focus3} performance to a named owner; reports without owners do not get acted on.`,
      ],
    },

    { type: "heading", content: pick(pitfallHeadings, seed + 4) },
    {
      type: "bullet-list",
      items: [
        `Treating ${catLower} as a vendor problem instead of an operational one — vendors execute, leaders direct.`,
        `Letting platform-default reports define what success looks like instead of grounding success in store-level outcomes.`,
        `Optimizing for clicks, impressions, or sessions when the real question is sold units or pipeline contribution.`,
        `Running too many small experiments at once so that nothing has enough volume to read out cleanly.`,
        `Hiring for production work and then asking that person to also do strategy. They are different jobs and require different skill sets.`,
        `Reviewing performance only quarterly without tracking weekly leading indicators that warn you when something is breaking.`,
        `Letting ${focus} live in a silo away from sales operations, fixed ops, and finance — the program will get cut the moment budgets tighten.`,
      ],
    },
    {
      type: "paragraph",
      content: `Most of these pitfalls are obvious in retrospect. In the moment they are easy to miss, especially when budgets are healthy and leadership is happy with surface-level reports. The discipline to ask the harder questions — and the willingness to act on the answers — is what separates the operations that compound from the ones that just stay busy. If a quarterly review is the first time anyone asks whether ${catLower} is working, the answer is already no.`,
    },

    { type: "heading", content: pick(rdHeadings, seed + 5) },
    {
      type: "paragraph",
      content: `Relevant Dealer is a fractional CMO practice serving auto OEMs, automotive SaaS companies, and dealer sales and service operations. We do not run an in-house creative shop, and we do not function as another vendor in the stack. Our job is to make sure ${catLower} programs are pointed at the right outcomes, instrumented honestly, and held to a measurable standard. When that is true, vendor relationships work better, internal teams do their best work, and leadership has a clear line of sight from strategy to sold units, gross profit, and retention.`,
    },
    {
      type: "bullet-list",
      items: [
        `Every engagement starts with a 30-day audit grounded in ${focus} and the rest of the marketing stack.`,
        `We document baseline outcome metrics before changing anything, so progress is measurable rather than narrative.`,
        `We run a quarterly operating rhythm with QBRs tied to sold units, pipeline, and retention — not vanity dashboards.`,
        `We coach internal teams to own the program. We are not trying to be permanent; we are trying to leave the team stronger than we found it.`,
        `We work transparently with existing vendors when they are good, and we replace them when they are not. Either way, the dealer is in control.`,
      ],
    },

  ];

  if (!hasExistingFaq) {
    sections.push(
      { type: "heading", content: `Frequently Asked Questions` },
      {
        type: "faq",
        qa: [
          {
            q: `How long until ${catLower} changes show up in revenue?`,
            a: `Most credible programs see leading-indicator movement inside 30 to 60 days and outcome-metric movement — sold units, qualified pipeline, retention — inside 90 to 180 days. Anyone promising faster is either selling you a tactic that already works in your account or measuring something that does not matter. The longer the buying cycle in your segment, the longer the lag, but the leading indicators should still move on the early timeline. If they are not moving by day 60, something is wrong with the program design and you should pause and diagnose before spending more.`,
          },
          {
            q: `Should we hire in-house or use an outside team for ${focus}?`,
            a: `It depends on scale and complexity. Single-point stores and small SaaS teams usually get more leverage from a fractional CMO plus a small set of specialist vendors than from hiring a full-time generalist. Larger dealer groups and OEM divisions tend to benefit from a hybrid: in-house leadership and orchestration, with specialist execution either in-house or through agencies. The wrong move is hiring a junior in-house person and asking them to do the work of a senior leader — that creates the appearance of progress without the substance.`,
          },
          {
            q: `What budget should we plan for ${catLower} in 2026?`,
            a: `Benchmarks vary widely by segment. Most healthy franchise dealers we work with allocate roughly two to three percent of total revenue to marketing in aggregate, with ${catLower} representing a meaningful slice depending on competitive intensity. Automotive SaaS companies are usually closer to ten to fifteen percent of revenue, weighted heavily toward demand generation and customer marketing. The right number for your operation depends on margin structure, growth targets, and how much of the work happens internally versus through outside partners.`,
          },
          {
            q: `How do we know our current ${catLower} program is actually working?`,
            a: `Look at three things. First, can your team articulate the one outcome metric the program is moving and show the trend line over the last four quarters? Second, are leading indicators improving in a way that predicts that outcome metric? Third, when you remove a tactic for a controlled period, does the outcome metric measurably decline? If the answer to all three is yes, the program is working. If any of them is uncertain, you have a measurement problem to fix before you have a strategy problem.`,
          },
        ],
      },
    );
  }

  sections.push(
    { type: "heading", content: pick(closeHeadings, seed + 6) },
    {
      type: "paragraph",
      content: `${cat} is not magic, and it is not optional. The dealers, OEMs, and automotive SaaS teams that win in 2026 will be the ones who treat ${focus} as an operational discipline — measured honestly, run on a fixed cadence, and tied to outcomes the executive team actually cares about. The tactics will keep changing. The platforms will keep adding features. What stays constant is the operating rhythm: audit, fix, optimize, review, repeat. If your team is ready to put that rhythm in place, Relevant Dealer can help — and if you already have it running cleanly, we will tell you that too. The point is operational truth, not a sales pitch.`,
    },
  );

  return sections;
}
